import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Clock,
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Flag
} from "lucide-react";

const Exam = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [exam, setExam] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showQuitDialog, setShowQuitDialog] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [startTime, setStartTime] = useState(null);

  const submitExam = useCallback(async () => {
    if (submitting) return;
    
    setSubmitting(true);
    try {
      const answersArray = [];
      for (let i = 0; i < exam.questions.length; i++) {
        answersArray.push(answers[i] !== undefined ? answers[i] : -1);
      }
      
      const timeSpent = exam.time_limit - timeRemaining;
      const response = await axios.post(
        `${API}/quiz/submit`,
        {
          quiz_id: exam.quiz_id,
          answers: answersArray,
          time_spent: timeSpent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      navigate("/quiz-result", { state: { result: response.data, category: "exam" } });
    } catch (error) {
      toast.error("Erreur lors de la soumission de l'examen");
      console.error(error);
      setSubmitting(false);
    }
  }, [exam, answers, timeRemaining, token, navigate, submitting]);

  useEffect(() => {
    const fetchExam = async () => {
      try {
        const response = await axios.get(`${API}/exam`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExam(response.data);
        setTimeRemaining(response.data.time_limit);
      } catch (error) {
        toast.error("Erreur lors du chargement de l'examen");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchExam();
  }, [token]);

  useEffect(() => {
    if (!examStarted || timeRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error("Temps écoulé !");
          submitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [examStarted, submitExam]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (timeRemaining < 60) return "danger";
    if (timeRemaining < 300) return "warning";
    return "";
  };

  const handleStartExam = () => {
    setExamStarted(true);
    setStartTime(Date.now());
  };

  const handleSelectAnswer = (answerIndex) => {
    setSelectedAnswer(answerIndex);
    setAnswers({ ...answers, [currentIndex]: answerIndex });
  };

  const handleNext = () => {
    if (currentIndex < exam.questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(answers[currentIndex + 1] ?? null);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedAnswer(answers[currentIndex - 1] ?? null);
    }
  };

  const handleQuestionClick = (index) => {
    setCurrentIndex(index);
    setSelectedAnswer(answers[index] ?? null);
  };

  const answeredCount = Object.keys(answers).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <div className="spinner"></div>
          <p className="text-slate-600">Préparation de l'examen...</p>
        </div>
      </div>
    );
  }

  if (!examStarted) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="exam-intro-page">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="text-center">
            <CardContent className="p-8">
              <div className="h-20 w-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Flag className="h-10 w-10 text-blue-900" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-slate-900 mb-4">
                Examen blanc
              </h1>
              <p className="text-slate-600 mb-6">
                Testez vos connaissances en conditions réelles avec 20 questions 
                sur l'ensemble des thèmes du test civique.
              </p>
              <div className="bg-slate-50 rounded-lg p-6 mb-8">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-900">20</p>
                    <p className="text-sm text-slate-500">Questions</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-900">30</p>
                    <p className="text-sm text-slate-500">Minutes</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-900">6</p>
                    <p className="text-sm text-slate-500">Catégories</p>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 text-left">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Important</p>
                    <p>Une fois l'examen commencé, le chronomètre ne peut pas être arrêté. 
                    Assurez-vous d'avoir 30 minutes devant vous.</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate("/dashboard")}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-blue-900 hover:bg-blue-800"
                  onClick={handleStartExam}
                  data-testid="start-exam-btn"
                >
                  Commencer l'examen
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const currentQuestion = exam.questions[currentIndex];
  const progressPercent = ((currentIndex + 1) / exam.questions.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="exam-page">
      {/* Exam Mode Header */}
      <div className="exam-mode">
        <div className="flex items-center gap-4">
          <Flag className="h-5 w-5" />
          <span className="font-medium">Examen blanc en cours</span>
        </div>
        <div className={`timer ${getTimerClass()}`}>
          <Clock className="h-4 w-4" />
          {formatTime(timeRemaining)}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:text-red-300"
          onClick={() => setShowQuitDialog(true)}
        >
          Quitter
        </Button>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-12">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">
              Question {currentIndex + 1} sur {exam.questions.length}
            </span>
            <span className="text-sm text-slate-500">
              {answeredCount} réponses
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Question Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {exam.questions.map((_, index) => (
            <button
              key={index}
              onClick={() => handleQuestionClick(index)}
              className={`h-8 w-8 rounded-lg text-sm font-medium transition-all ${
                index === currentIndex
                  ? 'bg-blue-900 text-white'
                  : answers[index] !== undefined
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`question-nav-${index}`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {/* Question Card */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-4">
              <span className={`category-badge ${currentQuestion.category}`}>
                {currentQuestion.category}
              </span>
            </div>
            <h2 className="quiz-question" data-testid="exam-question">
              {currentQuestion.question}
            </h2>
            
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectAnswer(index)}
                  className={`quiz-option ${selectedAnswer === index ? 'selected' : ''}`}
                  data-testid={`exam-option-${index}`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`h-8 w-8 rounded-full border-2 flex items-center justify-center text-sm font-medium ${
                      selectedAnswer === index 
                        ? 'border-blue-600 bg-blue-600 text-white' 
                        : 'border-slate-300 text-slate-500'
                    }`}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span>{option}</span>
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Précédent
          </Button>
          
          {currentIndex === exam.questions.length - 1 ? (
            <Button
              onClick={submitExam}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="submit-exam-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  Terminer l'examen
                  <CheckCircle className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-blue-900 hover:bg-blue-800"
            >
              Suivant
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </main>

      {/* Quit Dialog */}
      <AlertDialog open={showQuitDialog} onOpenChange={setShowQuitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitter l'examen ?</AlertDialogTitle>
            <AlertDialogDescription>
              Votre progression ne sera pas sauvegardée. Êtes-vous sûr de vouloir quitter ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuer l'examen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigate("/dashboard")}
              className="bg-red-600 hover:bg-red-700"
            >
              Quitter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Exam;
